import {useThemeColors} from '@nfq/colors';

import {getHrefForLinkType, getRelAttributes, getTargetAttribute} from 'Application/utils/linkFieldUtils';
import {isDefined} from 'Application/utils/typeGuards';

import type {HeroBlockType} from 'types/payload-types';

/**
 * Custom hook that processes hero block content and returns formatted data for rendering hero components.
 * This hook handles theme-based styling, button configuration, accessibility properties, and link attributes
 * for hero blocks. It determines which buttons should be displayed based on the button configuration and
 * extracts relevant link properties for each button.
 *
 * @param theme            The visual theme for the hero block, either 'dark' or 'light'.
 * @param buttons          The button configuration object from the hero block data.
 * @param isPresentational Flag indicating if the hero block should be treated as presentational content.
 * @returns An object containing processed hero content data including button properties, colors, and accessibility attributes.
 *
 * @example
 * ```tsx
 * const {
 *     headlineColor,
 *     hasButtonOne,
 *     ctaHref,
 *     ctaText,
 *     ariaProps
 * } = useHeroContent('dark', heroBlock.buttons, false);
 * ```
 */
export const useHeroContent = (
    theme: 'dark' | 'light',
    buttons: HeroBlockType['buttons'],
    isPresentational: boolean
) => {
    const colors = useThemeColors();
    const headlineColor = theme === 'dark' ? colors.textPrimary : colors.textLight;
    const hasButtonOne = ['onlyCtaButton', 'bothButtons'].includes(buttons?.headerButtons ?? 'noButton');
    const hasButtonTwo = ['bothButtons'].includes(buttons?.headerButtons ?? 'noButton');
    let ctaHref = '';
    let ctaRel;
    let ctaTarget;
    let ctaText = '';
    let secondHref = '';
    let secondRel;
    let secondTarget;
    let secondText = '';
    const ariaProps = isPresentational ? {
        ariaHidden: true,
        role: 'presentation'
    } : {};

    if (hasButtonOne && isDefined(buttons?.ctaButton)) {
        ctaHref = getHrefForLinkType(buttons.ctaButton);
        ctaRel = getRelAttributes(buttons.ctaButton);
        ctaTarget = getTargetAttribute(buttons.ctaButton);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ctaText = buttons.ctaButton.label ?? '';
    }

    if (hasButtonTwo && isDefined(buttons?.secondaryButton)) {
        secondHref = getHrefForLinkType(buttons.secondaryButton);
        secondRel = getRelAttributes(buttons.secondaryButton);
        secondTarget = getTargetAttribute(buttons.secondaryButton);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        secondText = buttons.secondaryButton.label ?? '';
    }

    return {
        ariaProps,
        ctaHref,
        ctaRel,
        ctaTarget,
        ctaText,
        hasButtonOne,
        hasButtonTwo,
        headlineColor,
        secondHref,
        secondRel,
        secondTarget,
        secondText
    };
};